import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

import {
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Users,
  UserCheck,
  Briefcase,
  LayoutDashboard,
  ArrowLeft,
} from "lucide-react";

import API from "../api";
import { getAuth } from "firebase/auth";

function EmployeesSection() {
  const auth = getAuth();
  const navigate = useNavigate();

  const { orgSlug, orgId, loading: authLoading } = useContext(AuthContext);

  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  /*
  ===============================
  FETCH EMPLOYEES (REALTIME)
  ===============================
  */

  useEffect(() => {
    if (authLoading || !orgId) return;

    const q = query(
      collection(db, "organizations", orgId, "users"),
      where("role", "==", "employee"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setEmployees(data);
    });

    return () => unsubscribe();
  }, [orgId, authLoading]);

  /*
  ===============================
  DELETE EMPLOYEE
  ===============================
  */

  const handleDelete = async (id) => {
    if (!window.confirm("Permanent delete? This will remove employee access.")) {
      return;
    }

    try {
      const user = auth.currentUser;

      if (!user) {
        alert("User not authenticated");
        return;
      }

      const token = await user.getIdToken(true);

      await API.delete(`employees/${id}/delete/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Employee deleted successfully");

    } catch (err) {
      console.error(err);
      alert("Delete failed: " + err.message);
    }
  };

  /*
  ===============================
  SEARCH FILTER
  ===============================
  */

  const filteredEmployees = employees.filter((emp) => {
    const term = searchTerm.toLowerCase();

    return (
      emp.personal?.fullName?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.professional?.department?.toLowerCase().includes(term) ||
      emp.professional?.designation?.toLowerCase().includes(term)
    );
  });

  /*
  ===============================
  UI
  ===============================
  */

  return (
    <div className="p-8 bg-[#F8FAFC] min-h-screen">

      {/* HEADER */}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div className="flex items-center gap-4">

          <button
            onClick={() => navigate(`/${orgSlug}/admin-dashboard`)}
            className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-lg transition-all"
          >
            <ArrowLeft size={24} />
          </button>

          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">
              Workforce Directory
            </h1>
            <p className="text-slate-500 text-sm">
              Manage corporate employees and system access
            </p>
          </div>
        </div>

        <div className="flex gap-3">

          <button
            onClick={() => navigate(`/${orgSlug}/admin-dashboard`)}
            className="hidden lg:flex items-center gap-2 px-4 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-xl"
          >
            <LayoutDashboard size={18} />
            Dashboard
          </button>

          <button
            onClick={() => navigate(`/${orgSlug}/employees/add`)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-2xl font-bold shadow-xl shadow-indigo-200 flex items-center gap-2"
          >
            <Plus size={20} />
            Onboard Employee
          </button>

        </div>
      </div>

      {/* STATS */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">

        <StatCard
          icon={<Users className="text-blue-600" />}
          label="Total Employees"
          count={employees.length}
          color="bg-blue-50"
        />

        <StatCard
          icon={<UserCheck className="text-emerald-600" />}
          label="Active Employees"
          count={employees.filter((e) => e.status === "active").length}
          color="bg-emerald-50"
        />

        <StatCard
          icon={<Briefcase className="text-amber-600" />}
          label="Departments"
          count={[...new Set(employees.map((e) => e.professional?.department))].length}
          color="bg-amber-50"
        />

      </div>

      {/* EMPLOYEE TABLE */}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">

        {/* SEARCH */}

        <div className="p-6 border-b border-slate-100">

          <div className="relative max-w-md">

            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />

            <input
              type="text"
              placeholder="Search employee..."
              className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
              onChange={(e) => setSearchTerm(e.target.value)}
            />

          </div>

        </div>

        {/* TABLE */}

        <table className="w-full">

          <thead className="bg-slate-50">

            <tr>
              <th className="px-8 py-5 text-left text-xs font-bold text-slate-400 uppercase">
                Employee
              </th>

              <th className="px-8 py-5 text-left text-xs font-bold text-slate-400 uppercase">
                Department
              </th>

              <th className="px-8 py-5 text-left text-xs font-bold text-slate-400 uppercase">
                Status
              </th>

              <th className="px-8 py-5 text-right text-xs font-bold text-slate-400 uppercase">
                Actions
              </th>
            </tr>

          </thead>

          <tbody className="divide-y divide-slate-100">

            {filteredEmployees.map((emp) => (

              <tr key={emp.id} className="hover:bg-slate-50">

                <td className="px-8 py-5">

                  <div className="flex items-center gap-4">

                    <div className="h-11 w-11 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
                      {emp.personal?.fullName?.charAt(0)}
                    </div>

                    <div>
                      <h4 className="font-bold text-slate-800">
                        {emp.personal?.fullName}
                      </h4>

                      <p className="text-xs text-slate-400">
                        {emp.email}
                      </p>
                    </div>

                  </div>

                </td>

                <td className="px-8 py-5 text-slate-600 font-semibold">
                  {emp.professional?.department || "N/A"}
                </td>

                <td className="px-8 py-5">

                  <span
                    className={`px-3 py-1 rounded-lg text-xs font-bold ${
                      emp.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {emp.status}
                  </span>

                </td>

                <td className="px-8 py-5">

                  <div className="flex justify-end gap-3">

                    <button
                      onClick={() =>
                        navigate(`/${orgSlug}/employees/${emp.id}`)
                      }
                      className="p-2 text-slate-400 hover:text-indigo-600"
                    >
                      <Eye size={18} />
                    </button>

                    <button
                      onClick={() =>
                        navigate(`/${orgSlug}/employees/edit/${emp.id}`)
                      }
                      className="p-2 text-slate-400 hover:text-amber-600"
                    >
                      <Edit2 size={18} />
                    </button>

                    <button
                      onClick={() => handleDelete(emp.id)}
                      className="p-2 text-slate-400 hover:text-red-600"
                    >
                      <Trash2 size={18} />
                    </button>

                  </div>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

    </div>
  );
}

/*
=====================
STAT CARD
=====================
*/

const StatCard = ({ icon, label, count, color }) => (
  <div className="p-6 rounded-[2rem] border border-slate-100 bg-white flex items-center gap-5 shadow-sm">

    <div className={`p-4 rounded-2xl ${color}`}>
      {icon}
    </div>

    <div>
      <p className="text-xs font-bold text-slate-400 uppercase">
        {label}
      </p>

      <h3 className="text-2xl font-black text-slate-800">
        {count}
      </h3>
    </div>

  </div>
);

export default EmployeesSection;